import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import Footer from '@/components/Footer';
import { 
  Users, 
  Shield, 
  Search, 
  Filter,
  MoreHorizontal,
  UserCheck,
  UserX,
  Edit,
  Trash2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';

interface UserData {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role: string;
  created_at: string;
  is_active?: boolean;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  pendingApprovals: number;
  newThisMonth: number;
}

export default function UserManagement() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [stats, setStats] = useState<UserStats>({
    totalUsers: 0,
    activeUsers: 0,
    pendingApprovals: 0,
    newThisMonth: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const { user } = useAuthenticatedUser();

  useEffect(() => {
    if (user?.id) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      // Get Supabase users
      const { data: supabaseUsers, error: supabaseError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (supabaseError) throw supabaseError;

      // Get internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_users')
        .select('*')
        .order('created_at', { ascending: false });

      // Combine and format users
      const allUsers: UserData[] = [
        ...(supabaseUsers || []).map(user => ({
          id: user.id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          role: user.role || 'player',
          created_at: user.created_at,
          is_active: true
        })),
        ...(internalUsers || []).map(user => ({
          id: user.id,
          username: user.username,
          first_name: null, // Internal users don't have separate name fields
          last_name: null,
          email: null, // Internal users don't have email field
          role: user.role || 'player',
          created_at: user.created_at,
          is_active: user.is_active
        }))
      ];

      setUsers(allUsers);

      // Calculate stats
      const totalUsers = allUsers.length;
      const activeUsers = allUsers.filter(u => u.is_active !== false).length;
      const thisMonth = new Date();
      thisMonth.setMonth(thisMonth.getMonth() - 1);
      const newThisMonth = allUsers.filter(u => new Date(u.created_at) > thisMonth).length;

      const { count: pendingCount } = await supabase
        .from('approval_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      setStats({
        totalUsers,
        activeUsers,
        pendingApprovals: pendingCount || 0,
        newThisMonth
      });
    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'venue': return 'default';
      case 'coach': return 'secondary';
      case 'team-owner': return 'outline';
      default: return 'secondary';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <MobileHeader />
        <div className="container mx-auto px-4 py-8 pt-20">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">Please log in to access user management</p>
            </CardContent>
          </Card>
        </div>
        <MobileBottomNav />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <MobileHeader />
      
      <div className="container mx-auto px-4 py-8 pt-20 pb-24 md:pb-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">User Management</h1>
            <p className="text-muted-foreground">
              Manage user accounts, roles, and permissions
            </p>
          </div>

          {/* User Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Users className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.totalUsers}</p>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <UserCheck className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.activeUsers}</p>
                    <p className="text-sm text-muted-foreground">Active Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Shield className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.pendingApprovals}</p>
                    <p className="text-sm text-muted-foreground">Pending Approvals</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Users className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.newThisMonth}</p>
                    <p className="text-sm text-muted-foreground">New This Month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Management */}
          <Tabs defaultValue="users" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="users">All Users</TabsTrigger>
              <TabsTrigger value="roles">Role Management</TabsTrigger>
              <TabsTrigger value="approvals">Pending Approvals</TabsTrigger>
            </TabsList>

            <TabsContent value="users" className="space-y-6">
              {/* Search and Filter */}
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="venue">Venue</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="team-owner">Team Owner</SelectItem>
                    <SelectItem value="player">Player</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* User List */}
              <div className="space-y-4">
                {filteredUsers.map((userData) => (
                  <Card key={userData.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h4 className="font-semibold">
                              {userData.first_name && userData.last_name 
                                ? `${userData.first_name} ${userData.last_name}`
                                : userData.username || 'Unknown User'
                              }
                            </h4>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>{userData.email}</span>
                              <span>•</span>
                              <span>Joined: {new Date(userData.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getRoleColor(userData.role)}>
                            {userData.role.charAt(0).toUpperCase() + userData.role.slice(1)}
                          </Badge>
                          <Badge variant={userData.is_active !== false ? "default" : "secondary"}>
                            {userData.is_active !== false ? "Active" : "Inactive"}
                          </Badge>
                          <Button size="sm" variant="outline">
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="roles" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Role Management</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-2">Admin</h4>
                        <p className="text-sm text-muted-foreground mb-2">Full system access</p>
                        <Badge variant="destructive">High Privileges</Badge>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-2">Venue Manager</h4>
                        <p className="text-sm text-muted-foreground mb-2">Manage venue operations</p>
                        <Badge variant="default">Medium Privileges</Badge>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-2">Coach</h4>
                        <p className="text-sm text-muted-foreground mb-2">Team management access</p>
                        <Badge variant="secondary">Standard Privileges</Badge>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-2">Player</h4>
                        <p className="text-sm text-muted-foreground mb-2">Basic user access</p>
                        <Badge variant="outline">Basic Privileges</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="approvals" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Pending Approval Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {stats.pendingApprovals} requests awaiting review
                    </p>
                    <div className="flex gap-4">
                      <Button>Review All Requests</Button>
                      <Button variant="outline">Bulk Approve</Button>
                      <Button variant="outline">Export List</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <MobileBottomNav />
      <Footer />
    </div>
  );
}